import { Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useGetResultsForExperimentQuery } from "src/api/services/experimentsApi";
import { MatchParam } from "src/constants/matches";
import Centered from "src/shared/Centered";

const COLORS = [
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC949",
  "#AF7AA1",
  "#FF9DA7",
  "#9C755F",
  "#BAB0AC",
];

const ExperimentResults = () => {
  const { experimentId } = useParams<MatchParam<"EXPERIMENT_ID">>();
  const { data: results } = useGetResultsForExperimentQuery(
    experimentId ?? skipToken,
  );

  const events = useMemo(() => {
    if (results == null || results.events == null) return [];
    return results.events;
  }, [results]);

  const keys = useMemo(() => {
    return Object.keys(events[0] || {}).filter((key) => key !== "name");
  }, [events]);

  return (
    <Stack gap={3} sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4">Results</Typography>
      </Stack>
      <Centered gap={2}>
        <Typography variant="h4">{`Total assigned: ${results?.total_assigned}`}</Typography>
        <BarChart
          style={{
            width: "100%",
            maxWidth: "700px",
            maxHeight: "70vh",
            aspectRatio: 1.618,
          }}
          responsive
          data={events}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis width="auto" />
          <Tooltip />
          <Legend />
          {keys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={COLORS[index % COLORS.length]}
              activeBar={{ fill: "gold", stroke: "purple" }}
              radius={[10, 10, 0, 0]}
            />
          ))}
        </BarChart>
      </Centered>
    </Stack>
  );
};

export default ExperimentResults;
